/**
 * The signed-in person's own sign-in security status (signInSecurityStatus: their requirements and their dates), for the
 * banner band's two sign-in notices: the two-step countdown (SignInRequirementBanner.tsx) and the email notice
 * (EmailVerifyBanner.tsx). One read per person, shared by both and kept for the page's life; the client loads on demand
 * through its one door (the band is on the first download, the client never is).
 *
 * Read again when it may have changed: on leaving Settings (where two-step is set up), at once; when the tab is shown
 * again (back from the authenticator app or the inbox), once the last read is a minute old. A read that fails keeps
 * what the last one said: a notice never vanishes, or appears, because one read did not go through.
 */
import { useEffect, useRef, useState } from 'react';
import type { SignInSecurityStatus } from '../lib/signInSecurity/signInSecurityAnswers';
import { loadSignInSecurityClient } from '../services/signInSecurity/signInSecurityClientDoor';

/** How old a read may be before the tab shown again reads it afresh. */
export const STATUS_FRESH_MS = 60_000;

interface Entry {
  at: number;
  value: SignInSecurityStatus | null;
  inFlight: boolean;
}

const store = new Map<string, Entry>();
const listeners = new Set<() => void>();

/** Reads the person's status unless a read is out, or (when not forced) one is fresh. */
function read(uid: string, force: boolean, nowMs: number): void {
  const had = store.get(uid);
  if (had?.inFlight) return;
  if (had && !force && nowMs - had.at < STATUS_FRESH_MS) return;
  store.set(uid, { at: had?.at ?? 0, value: had?.value ?? null, inFlight: true });
  void (async () => {
    let value = had?.value ?? null;
    try {
      const client = await loadSignInSecurityClient();
      const answer = await client.status();
      if (answer.ok) value = answer.value;
    } catch {
      /* the client could not be had: what the last read said stands */
    }
    store.set(uid, { at: Date.now(), value, inFlight: false });
    listeners.forEach((changed) => changed());
  })();
}

/** The person's status (null: not read yet, nobody signed in, or not asked for here). */
export function useSignInSecurityStatus(uid: string | null, enabled: boolean, onSettings: boolean): SignInSecurityStatus | null {
  const [, setRevision] = useState(0);
  useEffect(() => {
    const changed = () => setRevision((n) => n + 1);
    listeners.add(changed);
    return () => { listeners.delete(changed); };
  }, []);

  // The first read, and a fresh one as the person leaves Settings.
  const wasOnSettings = useRef(onSettings);
  useEffect(() => {
    const left = wasOnSettings.current && !onSettings;
    wasOnSettings.current = onSettings;
    if (enabled && uid) read(uid, left, Date.now());
  }, [enabled, uid, onSettings]);

  // The tab shown again.
  useEffect(() => {
    if (!enabled || !uid) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') read(uid, false, Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, uid]);

  return enabled && uid ? store.get(uid)?.value ?? null : null;
}

/** Tests only: forget every read. */
export function resetSignInSecurityStatusForTests(): void {
  store.clear();
}
